import React from "react";
import { SupText, SupTooltip } from "@repo/core-ui";
import { SupInfoCircle } from "@repo/core-ui";
import { HeroStats } from "@/types";
import CountUp from "react-countup";
import Link from "next/link";
import { Icons } from "../../UI/Icon";

interface BoostInfoPanelProps {
  totalBoost?: HeroStats["boosts"]["total"];
  baseBoost?: HeroStats["boosts"]["base"];
  tierBoost?: HeroStats["boosts"]["tier"];
  superfrenBoost?: HeroStats["boosts"]["superfren"];
}

interface BoostRowProps {
  value: number | undefined;
  label: string | React.ReactNode;
  isTotal?: boolean;
  isLast?: boolean;
  isLink?: boolean;
  showPlus?: boolean;
  href?: string;
}

const BoostRow = ({ value, label, isTotal, isLast, isLink, showPlus, href }: BoostRowProps) => (
  <>
    <div
      className={`relative flex items-center justify-center ${!isLast ? "border-b-0.5" : ""} border-r-0.5 border-primary-400 ${isTotal ? "bg-primary-100/90" : "bg-neutral-50"}`}
    >
      {isTotal && <Icons.star className=" h-9 w-9" />}
      <SupText className={isTotal ? "text-[32px] font-bold text-primary-700 lg:text-[48px]" : ""} family="mono" variant="body1" weight="regular">
        <CountUp decimals={1} end={value ?? 0} suffix="X" />
      </SupText>
      {showPlus && (
        <SupText className="absolute top-[-19px] text-2xl text-primary-600" family="sans" variant="body1" weight="bold">
          +
        </SupText>
      )}
    </div>
    <div className={`flex items-center justify-end ${!isLast ? "border-b-0.5" : ""} border-primary-400 ${isTotal ? "bg-neutral-50" : ""} p-4`}>
      <SupText
        className={`${isTotal ? "text-lg" : "text-[14px]"} uppercase text-neutral-${isTotal ? "950" : "900"}`}
        family="mono"
        variant="body1"
        weight={isTotal ? "bold" : "regular"}
      >
        {isLink ? (
          <Link className="underline" href={href || "#"}>
            {label}
          </Link>
        ) : (
          label
        )}
      </SupText>
    </div>
  </>
);

export default function BoostInfoPanel({ totalBoost, baseBoost, tierBoost, superfrenBoost }: BoostInfoPanelProps) {
  const boostData = [
    {
      value: totalBoost,
      label: "total boost",
      isTotal: true,
    },
    {
      value: baseBoost,
      label: (
        <span className="inline-flex items-center gap-1">
          Base boost
          <SupTooltip wrapper unmount portal color="primary" popperClassName="max-w-[25rem]" content="Your blended base rate across standard vaults and SuperVaults.">
            <SupInfoCircle />
          </SupTooltip>
        </span>
      ),
    },
    { value: tierBoost, label: "TVL Boost", showPlus: true },
    {
      value: superfrenBoost,
      href: "/season1/nftboost/",
      label: (
        <span className="inline-flex items-center gap-1 underline underline-offset-2 hover:no-underline">
          SUPERFREN NFT BOOST
          <SupTooltip wrapper unmount portal color="primary" popperClassName="max-w-[25rem]" content="Collect an entire tier of SuperFrens to get an additional boost">
            <SupInfoCircle />
          </SupTooltip>
        </span>
      ),
      isLink: true,
      isLast: true,
      showPlus: true,
    },
  ];

  return (
    <div className="">
      <div className="grid w-full grid-cols-[1fr_2fr] grid-rows-[minmax(112px,_auto)_3fr] overflow-hidden rounded-3xl rounded-tr-none border-0.5 border-primary-600">
        {boostData.map((boost, index) => (
          <BoostRow key={`boost-row-${index}`} {...boost} />
        ))}
      </div>
    </div>
  );
}
